import React, { useMemo, useRef, useState } from "react";
import { colors, fonts } from "./theme";
import { View, Text, Card, Button, ScreenHeader, ProgressBar } from "./ui";
import { Icon } from "./Icon";
import { t } from "./content";
import { useAppState } from "./state";
import { useI18n } from "./i18n";

const LETTERS = ["A", "B", "C", "D", "E", "F"];

function shuffled(q) {
  const arr = [...q.options];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function QuestionRunner({ title, subtitle, questions, onFinish, record = true, onExit }) {
  const { lang, tr } = useI18n();
  const { recordAnswer } = useAppState();
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const answersLog = useRef([]);
  const q = questions[index];
  const options = useMemo(() => shuffled(q), [q.id]); // eslint-disable-line
  const isMulti = q.type === "multi";

  const isCorrectAnswer = () => [...selected].sort().join(",") === [...q.correct].sort().join(",");

  const toggle = (id) => {
    if (submitted) return;
    if (isMulti) setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    else setSelected([id]);
  };

  const submit = () => {
    if (selected.length === 0) return;
    const ok = isCorrectAnswer();
    if (ok) setCorrectCount((c) => c + 1);
    if (record) recordAnswer(q, ok);
    answersLog.current.push({ qid: q.id, selected: [...selected], correct: ok });
    setSubmitted(true);
  };

  const next = () => {
    if (index + 1 >= questions.length) { onFinish(correctCount, questions.length, answersLog.current); return; }
    setIndex((i) => i + 1);
    setSelected([]);
    setSubmitted(false);
  };

  const optionState = (id) => {
    const isSel = selected.includes(id);
    const isCor = q.correct.includes(id);
    if (!submitted) return isSel ? "selected" : "idle";
    if (isCor) return "correct";
    if (isSel && !isCor) return "wrong";
    return "idle";
  };

  const styleFor = (state) => ({
    selected: { bg: colors.brandTertiary, border: colors.brandPrimary, fg: colors.onSurface, badge: colors.brandPrimary, badgeFg: colors.onBrandPrimary },
    correct: { bg: colors.successSurface, border: colors.success, fg: colors.onSurface, badge: colors.success, badgeFg: colors.onSuccess },
    wrong: { bg: colors.errorSurface, border: colors.error, fg: colors.onSurface, badge: colors.error, badgeFg: colors.onError },
    idle: { bg: colors.surfaceSecondary, border: colors.border, fg: colors.onSurface, badge: colors.surfaceTertiary, badgeFg: colors.muted },
  }[state]);

  const correct = isCorrectAnswer();

  return (
    <View style={{ flex: 1, backgroundColor: colors.surface, minHeight: "100vh" }}>
      <ScreenHeader title={title} subtitle={subtitle} onBack={onExit} />
      <View style={{ padding: "0 20px 12px" }}>
        <View row style={{ justifyContent: "space-between", marginBottom: 8, marginTop: 8 }}>
          <Text style={{ fontWeight: fonts.medium, fontSize: 13, color: colors.muted }}>
            {tr("question")} {index + 1} {tr("of")} {questions.length}
          </Text>
          <Text style={{ fontWeight: fonts.semibold, fontSize: 13, color: colors.brandPrimary }}>
            {Math.round((index / questions.length) * 100)}%
          </Text>
        </View>
        <ProgressBar progress={((index + (submitted ? 1 : 0)) / questions.length) * 100} />
      </View>
      <View style={{ padding: "0 20px 24px", flex: 1 }}>
        {q.scenario && (
          <Card style={{ marginBottom: 16, backgroundColor: colors.surfaceSecondary }}>
            {q.topic && <Text style={{ fontWeight: fonts.semibold, fontSize: 12, color: colors.brandPrimary, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{t(q.topic, lang)}</Text>}
            <Text style={{ fontWeight: fonts.regular, fontSize: 15, lineHeight: "23px", color: colors.onSurface }}>{t(q.scenario, lang)}</Text>
          </Card>
        )}
        <Text style={{ fontWeight: fonts.bold, fontSize: 20, lineHeight: "28px", color: colors.onSurface, marginBottom: 18 }}>{t(q.prompt, lang)}</Text>
        {isMulti && !submitted && (
          <Text style={{ fontWeight: fonts.medium, fontSize: 13, color: colors.muted, marginBottom: 12 }}>
            {lang === "de" ? "Mehrere Antworten möglich" : "Multiple answers possible"}
          </Text>
        )}
        <View style={{ gap: 10 }}>
          {options.map((opt, i) => {
            const st = optionState(opt.id);
            const s = styleFor(st);
            return (
              <View key={opt.id} row data-testid={`option-${opt.id}`} onClick={() => toggle(opt.id)} style={{
                alignItems: "center", gap: 12, backgroundColor: s.bg, border: `1.5px solid ${s.border}`,
                borderRadius: 16, padding: 14, cursor: submitted ? "default" : "pointer",
              }}>
                <View style={{ width: 30, height: 30, borderRadius: 15, backgroundColor: s.badge, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {st === "correct" ? <Icon name="check" size={16} color={s.badgeFg} weight="bold" />
                    : st === "wrong" ? <Icon name="x-circle" size={18} color={s.badgeFg} weight="fill" />
                    : <span style={{ fontWeight: fonts.bold, fontSize: 13, color: s.badgeFg }}>{LETTERS[i]}</span>}
                </View>
                <Text style={{ flex: 1, fontWeight: fonts.medium, fontSize: 15, lineHeight: "21px", color: s.fg }}>{t(opt.text, lang)}</Text>
              </View>
            );
          })}
        </View>
        {submitted && (
          <Card style={{ marginTop: 16, backgroundColor: correct ? colors.successSurface : colors.errorSurface, border: `1px solid ${correct ? colors.success : colors.error}` }}>
            <View row style={{ alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Icon name={correct ? "check-circle" : "x-circle"} size={22} color={correct ? colors.success : colors.error} weight="fill" />
              <Text style={{ fontWeight: fonts.bold, fontSize: 16, color: correct ? colors.onSuccessSurface : colors.onErrorSurface }}>{correct ? tr("correct") : tr("incorrect")}</Text>
            </View>
            <Text style={{ fontWeight: fonts.regular, fontSize: 14, lineHeight: "21px", color: colors.onSurface }}>{t(q.explanation, lang)}</Text>
          </Card>
        )}
      </View>
      <View style={{ padding: "10px 20px 20px", borderTop: `1px solid ${colors.divider}`, backgroundColor: colors.surface, position: "sticky", bottom: 0 }}>
        {!submitted ? (
          <Button testID="check-answer-btn" title={tr("submit")} onPress={submit} disabled={selected.length === 0} iconRight="arrow-right" />
        ) : (
          <Button testID="next-question-btn" title={index + 1 >= questions.length ? tr("finish") : tr("next")} onPress={next} iconRight="arrow-right" />
        )}
      </View>
    </View>
  );
}

export function ResultSummary({ correct, total, onDone, onRetry }) {
  const { tr } = useI18n();
  const pct = total ? Math.round((correct / total) * 100) : 0;
  const good = pct >= 60;
  return (
    <View style={{ padding: 20, gap: 16 }}>
      <Card style={{ alignItems: "center", paddingTop: 28, paddingBottom: 28 }}>
        <View style={{ width: 84, height: 84, borderRadius: 42, backgroundColor: good ? colors.successSurface : colors.warningSurface, alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
          <Icon name={good ? "confetti" : "target"} size={40} color={good ? colors.success : colors.warning} weight="fill" />
        </View>
        <Text style={{ fontWeight: fonts.bold, fontSize: 40, color: colors.onSurface }}>{pct}%</Text>
        <Text style={{ fontWeight: fonts.medium, fontSize: 15, color: colors.muted, marginTop: 4 }}>{correct} / {total} {tr("questions")}</Text>
      </Card>
      {onRetry && <Button title={tr("retry")} variant="secondary" icon="shuffle" onPress={onRetry} />}
      <Button title={tr("done")} onPress={onDone} iconRight="check" />
    </View>
  );
}
